import {useMemo} from 'react';

/**
 * The most instances a set may create in one request. Decided by product rather
 * than by the walker: above this, creation is refused with a message naming the
 * real figure. Must stay in step with RecurrenceExpander.MAX_OCCURRENCES.
 */
export const MAX_OCCURRENCES = 500;

/**
 * How far the client keeps counting purely so a refusal can name a number.
 * The ceiling stops the set being CREATED; it should not stop us telling the user
 * they asked for 520. This is date arithmetic with no I/O, so probing further is
 * cheap, and the bound keeps a badly-configured daily-for-a-decade rule from
 * spinning.
 */
export const PROBE_LIMIT = 2000;

export interface TaskSetFormState {
	frequency: 'DAILY' | 'WEEKLY' | 'MONTHLY' | 'YEARLY';
	intervalLength: number;
	/**
	 * WEEKLY only. Split from monthlyWeekday for the same reason RecurrenceRule
	 * splits them: a weekly rule falls on several days, a monthly nth-weekday rule
	 * falls on exactly one, and one field for both would make "the last Friday and
	 * Monday of the month" expressible.
	 */
	weekdays: string[];
	/** MONTHLY nth-weekday / last-weekday only. */
	monthlyWeekday: string | null;
	monthlyMode: 'DAY_OF_MONTH' | 'NTH_WEEKDAY' | 'LAST_WEEKDAY' | null;
	dayOfMonth: number | null;
	weekOrdinal: number | null;
	seedDate: string;
	boundType: 'COUNT' | 'DATE';
	boundCount: number | null;
	boundEndDate: string | null;
}

const DAY_ORDER = ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY', 'SUNDAY'];

/** Monday-based index, so it lines up with DAY_ORDER and with the backend. */
const mondayIndex = (d: Date): number => (d.getUTCDay() + 6) % 7;

const parseIso = (iso: string): Date => {
	const [year, month, day] = iso.split('-').map(Number);
	return new Date(Date.UTC(year, month - 1, day));
};

const toIso = (d: Date): string =>
	`${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`;

const lastDayOfMonth = (year: number, month: number): number => new Date(Date.UTC(year, month + 1, 0)).getUTCDate();

const nthWeekdayOfMonth = (year: number, month: number, weekday: string, ordinal: number): Date => {
	const target = DAY_ORDER.indexOf(weekday);
	const first = new Date(Date.UTC(year, month, 1));
	const offset = (target - mondayIndex(first) + 7) % 7;
	return new Date(Date.UTC(year, month, 1 + offset + (ordinal - 1) * 7));
};

const lastWeekdayOfMonth = (year: number, month: number, weekday: string): Date => {
	const target = DAY_ORDER.indexOf(weekday);
	const last = new Date(Date.UTC(year, month, lastDayOfMonth(year, month)));
	const back = (mondayIndex(last) - target + 7) % 7;
	return new Date(Date.UTC(year, month, last.getUTCDate() - back));
};

/** Monday-based indices of the selected weekdays, in week order. */
const selectedDayIndices = (weekdays: readonly (string | null)[] | null | undefined): number[] =>
	DAY_ORDER.map((_, i) => i).filter(i => (weekdays || []).includes(DAY_ORDER[i]));

/** The first date on or after `from` whose weekday is selected. */
const nextOrSameSelectedWeekday = (from: Date, weekdays: string[]): Date => {
	for (let i = 0; i < 7; i++) {
		const candidate = new Date(Date.UTC(from.getUTCFullYear(), from.getUTCMonth(), from.getUTCDate() + i));
		if (weekdays.includes(DAY_ORDER[mondayIndex(candidate)])) {
			return candidate;
		}
	}
	return from;
};

/**
 * Mirrors RecurrenceExpander.weeklyOccurrence. The period-th occurrence counted
 * from `first`, not the next one after some cursor.
 *
 * Indexed because the caller asks for an arbitrary period: the slot within the
 * selected-day cycle gives the weekday, and how many times the cycle has wrapped
 * gives the weeks, times the interval. So the interval spaces the WEEKS, not the
 * occurrences -- "every two weeks on Mon and Wed" is Mon, Wed, skip a week, Mon,
 * Wed. Written iteratively it would return the same date for every period once
 * the answer fell inside first's own week.
 */
const weeklyOccurrence = (form: TaskSetFormState, picked: number[], first: Date, period: number): Date => {
	if (picked.length === 0) {
		return first;
	}
	// nextOrSameSelectedWeekday put `first` on a selected day, so this is never -1.
	const firstIndex = picked.indexOf(mondayIndex(first));
	const slot = firstIndex + period;
	const weeksAhead = Math.floor(slot / picked.length) * form.intervalLength;
	const dayInWeek = picked[slot % picked.length];

	const weekStart = first.getUTCDate() - mondayIndex(first);
	return new Date(Date.UTC(first.getUTCFullYear(), first.getUTCMonth(), weekStart + weeksAhead * 7 + dayInWeek));
};

/**
 * Mirrors RecurrenceExpander.cycleOf: which cycle of the pattern the period-th
 * occurrence falls in, counting from 0.
 *
 * "Ends after N times" means N cycles, not N tasks. Only a weekly rule on several
 * days has more than one occurrence per cycle, so for every other rule the two
 * are the same number. The week holding `first` is cycle 0 even when it is
 * partial, so it counts as one of the N.
 */
const cycleOf = (form: TaskSetFormState, picked: number[], first: Date, period: number): number => {
	if (form.frequency !== 'WEEKLY') {
		return period;
	}
	return Math.floor((picked.indexOf(mondayIndex(first)) + period) / picked.length);
};

/** The calendar month `step` months after `from`, as a year/month pair. */
const monthsFrom = (from: Date, step: number): {year: number; month: number} => {
	const monthIndex = from.getUTCMonth() + step;
	return {
		year: from.getUTCFullYear() + Math.floor(monthIndex / 12),
		month: ((monthIndex % 12) + 12) % 12,
	};
};

/**
 * Mirrors RecurrenceExpander.monthlyOccurrence: resolves the rule's day within a
 * given month. Only the month matters -- the day the caller arrived with is
 * discarded, which is why "the last Friday" lands correctly in every month
 * rather than drifting from the seed's own date.
 */
const monthlyOccurrenceIn = (form: TaskSetFormState, {year, month}: {year: number; month: number}): Date => {
	if (form.monthlyMode === 'NTH_WEEKDAY') {
		return nthWeekdayOfMonth(year, month, form.monthlyWeekday as string, form.weekOrdinal as number);
	}
	if (form.monthlyMode === 'LAST_WEEKDAY') {
		return lastWeekdayOfMonth(year, month, form.monthlyWeekday as string);
	}
	// Clamp rather than roll: "the 31st" in February means the 28th, not 3 March.
	return new Date(Date.UTC(year, month, Math.min(form.dayOfMonth as number, lastDayOfMonth(year, month))));
};

const occurrenceForPeriod = (form: TaskSetFormState, picked: number[], seed: Date, period: number): Date => {
	const step = form.intervalLength * period;

	if (form.frequency === 'DAILY') {
		return new Date(Date.UTC(seed.getUTCFullYear(), seed.getUTCMonth(), seed.getUTCDate() + step));
	}
	if (form.frequency === 'WEEKLY') {
		return weeklyOccurrence(form, picked, seed, period);
	}
	if (form.frequency === 'YEARLY') {
		// Clamp rather than roll, matching Java's plusYears: 29 February in a
		// non-leap year is 28 February. Date.UTC would roll it to 1 March and the
		// preview would name dates the backend never creates.
		const yearlyYear = seed.getUTCFullYear() + step;
		const yearlyMonth = seed.getUTCMonth();
		return new Date(
			Date.UTC(yearlyYear, yearlyMonth, Math.min(seed.getUTCDate(), lastDayOfMonth(yearlyYear, yearlyMonth)))
		);
	}

	return monthlyOccurrenceIn(form, monthsFrom(seed, step));
};

/**
 * Mirrors RecurrenceExpander.firstOccurrence: the earliest date satisfying the
 * rule that is not before the seed.
 *
 * The seed is where the series starts looking, not necessarily its first
 * occurrence. Both the weekly and the monthly rule snap FORWARD, so a set never
 * starts earlier than the date the user picked -- and instance one always obeys
 * the pattern the config view describes.
 */
const firstOccurrence = (form: TaskSetFormState, seed: Date): Date => {
	if (form.frequency === 'WEEKLY') {
		return form.weekdays.length > 0 ? nextOrSameSelectedWeekday(seed, form.weekdays) : seed;
	}
	// A null mode is a half-built form; there is no rule to satisfy yet.
	if (form.frequency === 'MONTHLY' && form.monthlyMode) {
		const candidate = monthlyOccurrenceIn(form, monthsFrom(seed, 0));
		return candidate.getTime() < seed.getTime() ? monthlyOccurrenceIn(form, monthsFrom(seed, 1)) : candidate;
	}
	return seed;
};

/**
 * Mirrors RecurrenceExpander on the backend. Kept in step with it by sharing the
 * same test cases -- if one changes, both change.
 */
export const expandRecurrence = (form: TaskSetFormState): string[] => {
	if (!form.seedDate) {
		return [];
	}
	if (form.frequency === 'WEEKLY' && form.weekdays.length === 0) {
		return [];
	}
	if (!form.intervalLength || form.intervalLength < 1) {
		// An interval below one never advances, so the walk would run to the probe
		// limit. Validation refuses this too; returning nothing keeps the preview
		// from spinning while the user is mid-edit.
		return [];
	}

	// Snapped to the first date the rule actually allows, so period 0 obeys the
	// pattern rather than being the raw seed.
	const first = firstOccurrence(form, parseIso(form.seedDate));
	const end = form.boundType === 'DATE' && form.boundEndDate ? parseIso(form.boundEndDate) : null;
	// Fixed for the whole walk, so worked out once rather than per period.
	const picked = selectedDayIndices(form.weekdays);

	const dates: string[] = [];
	let current = first;
	let period = 0;

	// Counts past the ceiling on purpose, up to PROBE_LIMIT, so an over-ceiling
	// rule can be reported as "520 tasks" rather than "more than 500".
	while (dates.length < PROBE_LIMIT) {
		if (form.boundType === 'COUNT' && cycleOf(form, picked, first, period) >= (form.boundCount || 0)) {
			break;
		}
		if (end && current.getTime() > end.getTime()) {
			break;
		}

		dates.push(toIso(current));
		period += 1;
		current = occurrenceForPeriod(form, picked, first, period);
	}

	return dates;
};

export interface TaskSetPreview {
	dates: string[];
	/** What the rule asks for, which may be above the ceiling. */
	count: number;
	overCeiling: boolean;
	/** True when the count is itself a floor rather than exact -- see PROBE_LIMIT. */
	countIsAtProbeLimit: boolean;
}

export const useTaskSetPreview = (form: TaskSetFormState): TaskSetPreview =>
	useMemo(() => {
		const dates = expandRecurrence(form);
		const overCeiling = dates.length > MAX_OCCURRENCES;
		return {
			// Never show occurrences that would not be created as if they were.
			dates: overCeiling ? dates.slice(0, MAX_OCCURRENCES) : dates,
			count: dates.length,
			overCeiling,
			countIsAtProbeLimit: dates.length >= PROBE_LIMIT,
		};
	}, [form]);

/** The stored fields `plannedInstanceCount` needs. Structural, like DescribableTaskSet. */
export interface CountableTaskSet {
	frequency?: string | null;
	weekdays?: readonly (string | null)[] | null;
	seedDate?: string | null;
	boundType?: string | null;
	boundCount?: number | null;
}

/**
 * How many instances a count-bounded set's rule produces, or null where the rule
 * states no length.
 *
 * The stored count is a number of cycles, not of instances. For every rule but a
 * weekly one on several days a cycle holds one instance, so the count is the
 * answer. A weekly cycle holds one instance per selected day, less the selected
 * days that fell before the first instance in week 1: "Mon, Tue, Wed, 2 times"
 * from a Wednesday is Wed, then Mon to Wed -- four, not six.
 *
 * Null for a weekly set whose weekdays are missing, or a weekly set on several
 * days whose seed is missing: without them the number of instances per week, or
 * the partial first week, is unknowable. Answering with the stored count instead
 * would state a number of weeks as a number of instances and mislabel every chip,
 * so a caller that forgets to select these fields gets no total rather than a
 * wrong one.
 */
export const plannedInstanceCount = (set: CountableTaskSet): number | null => {
	if (set.boundType !== 'COUNT' || set.boundCount == null) {
		return null;
	}
	if (set.frequency === 'WEEKLY' && set.weekdays == null) {
		return null;
	}
	const picked = selectedDayIndices(set.weekdays);
	if (set.frequency !== 'WEEKLY' || picked.length <= 1) {
		return set.boundCount;
	}
	if (!set.seedDate) {
		return null;
	}
	const first = nextOrSameSelectedWeekday(
		parseIso(set.seedDate),
		picked.map(i => DAY_ORDER[i])
	);
	const skippedInFirstWeek = picked.indexOf(mondayIndex(first));
	return set.boundCount * picked.length - skippedInFirstWeek;
};

/**
 * What the drawer says about week 1 when it holds fewer than every selected day.
 *
 * Only a weekly rule on several days can have a partial first week, and only
 * when the seed lands after the first selected day of its own week. A seed past
 * the LAST selected day snaps to the next week, which is full, so there is
 * nothing to warn about.
 */
export interface PartialFirstWeek {
	/** The seed date, as picked -- which is also the first instance. */
	seedDate: string;
	/** The selected days that week 1 actually holds, Monday first. */
	daysInFirstWeek: string[];
	/**
	 * The first selected day of the following week: where a full week 1 would
	 * start. Null when an end date falls before it, because starting there would
	 * leave the set with no instances at all.
	 */
	fullWeekStart: string | null;
}

export const partialFirstWeek = (form: TaskSetFormState): PartialFirstWeek | null => {
	if (form.frequency !== 'WEEKLY' || !form.seedDate) {
		return null;
	}
	const picked = selectedDayIndices(form.weekdays);
	if (picked.length <= 1) {
		return null;
	}
	const seed = parseIso(form.seedDate);
	// A half-typed date parses to NaN, which fails both range checks below and
	// would fall through to a nonsense alert.
	if (isNaN(seed.getTime())) {
		return null;
	}
	const seedIndex = mondayIndex(seed);
	if (seedIndex <= picked[0] || seedIndex > picked[picked.length - 1]) {
		return null;
	}
	const weekStart = new Date(Date.UTC(seed.getUTCFullYear(), seed.getUTCMonth(), seed.getUTCDate() - seedIndex));
	const nextWeekFirstDay = new Date(
		Date.UTC(weekStart.getUTCFullYear(), weekStart.getUTCMonth(), weekStart.getUTCDate() + 7 + picked[0])
	);
	const endsBeforeFullWeek =
		form.boundType === 'DATE' && !!form.boundEndDate && parseIso(form.boundEndDate).getTime() < nextWeekFirstDay.getTime();
	return {
		seedDate: form.seedDate,
		daysInFirstWeek: picked.filter(i => i >= seedIndex).map(i => DAY_ORDER[i]),
		fullWeekStart: endsBeforeFullWeek ? null : toIso(nextWeekFirstDay),
	};
};
